import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, FormikProvider, useFormik } from "formik";
import { Icon } from "@iconify/react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import { Loader } from "../../Loader";
import { StripePaymentModule } from "src/common/components/StripePaymentModule";
import { PaytmPaymentModule } from "src/common/components/PaytmPaymentModule";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import sanitizeValues from "src/utils/sanitizeValues";
import {
  getTeamUrl,
  fleetWalletUrl,
  fleetWalletTopupUrl,
  fleetWalletAdjustmentUrl,
  initiateFleetWalletTopupUrl,
} from "src/react-query/endPoints";
import {
  toList,
  listTotal,
  statusChipColor,
  ledgerTypeChipColor,
  formatMoney,
  walletTopupValidationSchema,
  walletAdjustmentValidationSchema,
} from "../fleet.constants";

const getLedgerId = (row) => row?.ledger_id ?? row?.ledgerId ?? row?.id;

const AmountDialog = ({
  open,
  onClose,
  title,
  contentHint,
  validationSchema,
  onConfirm,
  isLoading,
  okText,
}) => {
  const formik = useFormik({
    initialValues: { amount: "", remarks: "" },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      onConfirm(
        {
          amount: Number(values.amount),
          remarks: sanitizeValues(values.remarks) || undefined,
        },
        () => resetForm()
      );
      setSubmitting(false);
    },
  });
  const { errors, touched, handleSubmit, getFieldProps, resetForm } = formik;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              {contentHint && (
                <Typography variant="body2" color="text.secondary">
                  {contentHint}
                </Typography>
              )}
              <TextField
                size="small"
                label="Amount"
                type="number"
                required
                {...getFieldProps("amount")}
                error={Boolean(touched.amount && errors.amount)}
                helperText={touched.amount && errors.amount}
              />
              <TextField
                size="small"
                label="Remarks"
                multiline
                rows={2}
                {...getFieldProps("remarks")}
                error={Boolean(touched.remarks && errors.remarks)}
                helperText={touched.remarks && errors.remarks}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton type="submit" variant="contained" loading={isLoading}>
              {okText}
            </LoadingButton>
          </DialogActions>
        </Form>
      </FormikProvider>
    </Dialog>
  );
};

const WalletTab = ({ operatorId }) => {
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasUpdateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "U");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [topupOpen, setTopupOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [paymentTopupOpen, setPaymentTopupOpen] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // The wallet GET intentionally 404s when no wallet exists yet, so we use a
  // bare useQuery (no error toast) instead of useGetDetail.
  const {
    data: wallet,
    isFetching: isWalletFetching,
    error: walletError,
    refetch: refetchWallet,
  } = useQuery(queryKeys.fleetWallet(teamId, operatorId), fetchWithError, {
    enabled: Boolean(teamId && operatorId),
    select: selector,
    retry: false,
  });

  const hasWallet = Boolean(wallet) && !walletError;
  const isNoWallet = walletError?.response?.status === 404;

  const {
    data: ledgerData,
    isFetching: isLedgerFetching,
    refetch: refetchLedger,
  } = useQuery(
    queryKeys.fleetWalletLedger(teamId, operatorId, {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    }),
    fetchWithError,
    {
      enabled: Boolean(teamId && operatorId) && hasWallet,
      select: selector,
      keepPreviousData: true,
    }
  );

  const ledger = useMemo(() => toList(ledgerData), [ledgerData]);
  const ledgerTotal = listTotal(ledgerData, ledger.length);

  const { mutate, isLoading } = useMutate("fleetWallet", { id: operatorId });

  const refreshAll = () => {
    refetchWallet();
    refetchLedger();
  };

  const onCreateWallet = () => {
    mutate(
      {
        url: getTeamUrl(teamId, fleetWalletUrl(operatorId)),
        method: "POST",
        body: {},
      },
      { onSuccess: refreshAll }
    );
  };

  const onTopup = (body, resetDialogForm) => {
    mutate(
      {
        url: getTeamUrl(teamId, fleetWalletTopupUrl(operatorId)),
        method: "POST",
        body,
      },
      {
        onSuccess: () => {
          setTopupOpen(false);
          resetDialogForm();
          refreshAll();
        },
      }
    );
  };

  const onInitiateTopup = ({ amount }, resetDialogForm) => {
    mutate(
      {
        url: initiateFleetWalletTopupUrl,
        method: "POST",
        body: {
          company_id: teamId,
          user_id: user?.id,
          tag_id: wallet?.external_wallet_ref,
          currency: wallet?.currency ?? "INR",
          amount,
          for_fleet: true,
          redirect_to: `${window.location.pathname}?tab=wallet`,
        },
      },
      {
        onSuccess: (data) => {
          setPaymentTopupOpen(false);
          resetDialogForm?.();
          setPaymentData({ ...data, amount });
        },
      }
    );
  };

  const onAdjustment = (body, resetDialogForm) => {
    mutate(
      {
        url: getTeamUrl(teamId, fleetWalletAdjustmentUrl(operatorId)),
        method: "POST",
        body,
      },
      {
        onSuccess: () => {
          setAdjustmentOpen(false);
          resetDialogForm();
          refreshAll();
        },
      }
    );
  };

  const ledgerColumns = useMemo(
    () => [
      {
        field: "transaction_type",
        headerName: "Type",
        flex: 0.7,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ?? "-"}
            color={ledgerTypeChipColor(value)}
          />
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        flex: 0.8,
        renderCell: ({ value }) => formatMoney(value, wallet?.currency),
      },
      {
        field: "balance_after",
        headerName: "Balance After",
        flex: 0.8,
        renderCell: ({ value }) => formatMoney(value, wallet?.currency),
      },
      {
        field: "reference_id",
        headerName: "Reference",
        flex: 1,
        renderCell: ({ row }) =>
          row?.reference_id
            ? `${row?.reference_type ?? ""} ${row.reference_id}`.trim()
            : "-",
      },
      {
        field: "remarks",
        headerName: "Remarks",
        flex: 1,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "created_at",
        headerName: "Date",
        flex: 0.9,
        renderCell: ({ value }) => fDateTime(value) || "-",
      },
    ],
    [wallet?.currency]
  );

  if (isWalletFetching && !wallet) {
    return <Loader variant="skeleton" skeletonVariant="kpi" skeletonCount={2} />;
  }

  if (!hasWallet) {
    return (
      <Card variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>
          No Wallet Yet
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {isNoWallet
            ? "This fleet operator does not have a prepaid wallet. Create one to enable prepaid charging."
            : "Wallet details could not be loaded. Please try again."}
        </Typography>
        {isNoWallet && hasCreateAccess && !isScopedOperator && (
          <LoadingButton
            variant="contained"
            startIcon={<Icon icon="eva:plus-fill" />}
            loading={isLoading}
            onClick={onCreateWallet}
          >
            Create Wallet
          </LoadingButton>
        )}
        {!isNoWallet && (
          <Button variant="outlined" onClick={() => refetchWallet()}>
            Retry
          </Button>
        )}
      </Card>
    );
  }

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Card variant="outlined" sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Wallet Balance
          </Typography>
          <Typography variant="h4">
            {formatMoney(wallet?.balance, wallet?.currency)}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Chip size="small" variant="outlined" label={wallet?.currency ?? "INR"} />
            <Chip
              size="small"
              label={wallet?.status ?? "-"}
              color={statusChipColor(wallet?.status)}
            />
          </Stack>
        </Card>
        {hasUpdateAccess && !isScopedOperator && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Icon icon="eva:plus-circle-outline" />}
              onClick={() => setTopupOpen(true)}
            >
              Top-up
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon icon="eva:options-2-outline" />}
              onClick={() => setAdjustmentOpen(true)}
            >
              Adjustment
            </Button>
          </Stack>
        )}
        {isScopedOperator && (
          <Button
            variant="contained"
            startIcon={<Icon icon="eva:plus-circle-outline" />}
            onClick={() => setPaymentTopupOpen(true)}
          >
            Top-up
          </Button>
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" mb={2}>
        Ledger
      </Typography>
      <BaseTable
        rows={ledger}
        columns={ledgerColumns}
        loading={isLedgerFetching}
        getRowId={(row) => getLedgerId(row)}
        pageSize={paginationModel.pageSize}
        serverRowFetcher
        totalRows={ledgerTotal}
        paginationModel={paginationModel}
        onPageChange={(page) =>
          setPaginationModel((prev) => ({ ...prev, page }))
        }
        onPageSizeChange={(pageSize) =>
          setPaginationModel((prev) => ({ ...prev, pageSize, page: 0 }))
        }
      />
      {!isLedgerFetching && !ledger.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No wallet transactions yet.
        </Typography>
      )}

      <AmountDialog
        open={topupOpen}
        onClose={() => setTopupOpen(false)}
        title="Wallet Top-up"
        contentHint="Credits the fleet operator wallet."
        validationSchema={walletTopupValidationSchema}
        onConfirm={onTopup}
        isLoading={isLoading}
        okText="Top-up"
      />
      <AmountDialog
        open={adjustmentOpen}
        onClose={() => setAdjustmentOpen(false)}
        title="Wallet Adjustment"
        contentHint="Use a negative amount to debit, positive to credit. Remarks are required."
        validationSchema={walletAdjustmentValidationSchema}
        onConfirm={onAdjustment}
        isLoading={isLoading}
        okText="Apply Adjustment"
      />
      <AmountDialog
        open={paymentTopupOpen}
        onClose={() => setPaymentTopupOpen(false)}
        title="Top-up Wallet"
        contentHint={`Enter the amount to add to your wallet (${wallet?.currency ?? "INR"}). You will be redirected to complete payment.`}
        validationSchema={walletTopupValidationSchema}
        onConfirm={onInitiateTopup}
        isLoading={isLoading}
        okText="Proceed to Pay"
      />
      <StripePaymentModule
        open={paymentData?.for === "stripe" && Boolean(paymentData?.clientId)}
        onClose={() => setPaymentData(null)}
        clientId={paymentData?.clientId}
        paymentId={paymentData?.paymentId}
        returnUrl={paymentData?.return_url}
        onError={() => setPaymentData(null)}
      />
      <PaytmPaymentModule
        open={paymentData?.for === "paytm" && Boolean(paymentData?.txnToken)}
        onClose={() => setPaymentData(null)}
        txnId={paymentData?.txnToken}
        orderId={paymentData?.orderId}
        merchantId={paymentData?.merchantId}
        amount={paymentData?.amount}
      />
    </>
  );
};

export default WalletTab;
